import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The widget half of the host-page bridge (client/public/embed.js is the other half).
 * Owns the single `message` listener, so there is exactly one place an inbound origin is
 * checked, and drives the client-provided tool round-trip:
 *
 *   status poll says `waiting_for_client`
 *     → auto tools dispatched to the page immediately
 *     → `confirm` tools surfaced for the visitor to Allow or Deny first
 *     → page posts results back
 *     → once every call in the pause has an outcome, they go to the server as one batch
 *
 * ORIGIN. `hostOrigin` is the embedding origin the server matched against the key's
 * allowlist at /resolve — the only call where Referer means anything. Inbound messages are
 * checked against it and outbound ones target it. This matters far more than it did when
 * the bridge only carried `send`: a forged `send` is no more powerful than the visitor
 * typing, but a forged TOOL RESULT is text the model then reasons over as fact. When the
 * allowlist is empty the server sends null and this falls back to accepting any origin —
 * permissive, but only in the configuration that was already permissive by design.
 *
 * BATCHING. Results are held until the whole pause is answered because the server resumes
 * the turn on the first POST; sending them one at a time would resume with the rest still
 * missing, and those calls would come back to the model as "the page returned no result".
 *
 * CAPABILITIES. embed.js is a cached static file on a customer's site, so an arbitrarily
 * old copy may be the one running. `hostFeatures` is what THIS host answered with, and it
 * starts empty — a control gated on it is simply never rendered against a host that cannot
 * honour it, instead of becoming a button that does nothing.
 */
const HOST_CONFIG_KEYS = ['theme', 'title', 'logo', 'showFooter', 'tts'];
const MAX_CONFIG_TITLE = 60;

/** The host's `configure()` payload is page-authored, so it is read through a fixed key
 *  list rather than spread — nothing the page invents reaches component state. Own
 *  properties only: a prototype-chain hit is never a value the page meant to send. */
export function readHostConfig(data) {
  const config = {};
  for (const key of HOST_CONFIG_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(data, key) || data[key] === undefined) continue;
    if (key === 'showFooter' || key === 'tts') config[key] = !!data[key];
    else if (key === 'title') config[key] = String(data[key]).slice(0, MAX_CONFIG_TITLE);
    else config[key] = String(data[key]);
  }
  return Object.keys(config).length ? config : null;
}

export function useClientToolBridge({
  status, hostOrigin, pendingJob, onSend, onResults, onToolsToken, onPageContext, onConfig,
}) {
  const [awaitingConfirm, setAwaitingConfirm] = useState([]);
  const [hostFeatures, setHostFeatures] = useState([]);
  const [hostMobile, setHostMobile] = useState(false);
  const expectedRef = useRef(new Set());
  const resultsRef = useRef(new Map());
  const activeJobRef = useRef(null);
  const onResultsRef = useRef(onResults);
  onResultsRef.current = onResults;
  // Read inside the listener effect, which must not re-subscribe (and re-post `ready`)
  // every time the page hands us a new inline callback.
  const onConfigRef = useRef(onConfig);
  onConfigRef.current = onConfig;

  const targetOrigin = hostOrigin || '*';

  const postToHost = useCallback((message) => {
    window.parent.postMessage({ source: 'agent-widget', v: 1, ...message }, targetOrigin);
  }, [targetOrigin]);

  /** Records one call's outcome and flushes the batch once the pause is fully answered. */
  const recordResult = useCallback((result) => {
    if (!expectedRef.current.has(result.callId)) return;
    resultsRef.current.set(result.callId, result);
    if (resultsRef.current.size < expectedRef.current.size) return;
    const batch = [...resultsRef.current.values()];
    expectedRef.current = new Set();
    resultsRef.current = new Map();
    setAwaitingConfirm([]);
    onResultsRef.current(batch);
  }, []);

  const dispatch = useCallback((call) => {
    postToHost({ type: 'invoke-tool', callId: call.callId, name: call.name, args: call.args || {} });
  }, [postToHost]);

  /** Visitor's decision on a `confirm` call. A denial never reaches the page's handler —
   *  the point of asking is that the action does not happen. */
  const decideConfirm = useCallback((call, allowed) => {
    setAwaitingConfirm((current) => current.filter((c) => c.callId !== call.callId));
    if (allowed) dispatch(call);
    else recordResult({ callId: call.callId, ok: true, denied: true });
  }, [dispatch, recordResult]);

  // One listener for the whole bridge.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    // A route table rather than an if/else chain: unknown types must stay a no-op (that is
    // what lets either side of the bridge grow independently), and each branch stays small
    // enough to read at a glance as the protocol keeps accreting message types.
    const routes = {
      send: (data) => { if (typeof data.text === 'string' && data.text.trim()) onSend(data.text); },
      tools: (data) => onToolsToken(data.token || null),
      'page-context': (data) => onPageContext(data.context || null),
      'tool-result': (data) => {
        if (!data.callId) return;
        recordResult({
          callId: data.callId,
          ok: data.ok !== false,
          result: data.result,
          error: data.error,
          durationMs: data.durationMs,
        });
      },
      'host-hello': (data) => {
        setHostFeatures(Array.isArray(data.features) ? data.features : []);
        setHostMobile(!!data.mobile);
      },
      config: (data) => {
        const config = readHostConfig(data);
        if (config && onConfigRef.current) onConfigRef.current(config);
      },
    };
    const onHostMessage = (e) => {
      if (hostOrigin && e.origin !== hostOrigin) return;
      if (!e.data || e.data.source !== 'agent-widget-host') return;
      const route = routes[e.data.type];
      if (route) route(e.data);
    };
    window.addEventListener('message', onHostMessage);
    postToHost({ type: 'ready' });
    return () => window.removeEventListener('message', onHostMessage);
  }, [status, hostOrigin, onSend, onToolsToken, onPageContext, recordResult, postToHost]);

  // A new `waiting_for_client` pause: dispatch what needs no permission, ask about the
  // rest. Guarded by job id because the status poll re-delivers the same pause every
  // 1.5s — without it every tick would re-run the page's handlers.
  useEffect(() => {
    const calls = pendingJob?.status === 'waiting_for_client' ? pendingJob.clientCalls : null;
    if (!calls?.length) return;
    if (activeJobRef.current === pendingJob.id) return;
    activeJobRef.current = pendingJob.id;

    expectedRef.current = new Set(calls.map((c) => c.callId));
    resultsRef.current = new Map();
    setAwaitingConfirm(calls.filter((c) => c.confirm));
    calls.filter((c) => !c.confirm).forEach(dispatch);
  }, [pendingJob, dispatch]);

  const hasFeature = useCallback((name) => hostFeatures.includes(name), [hostFeatures]);

  // Outbound helpers rather than a raw postToHost: every message the widget sends leaves
  // through this module, so the origin pinning above stays the single gate it claims to be.
  const requestResize = useCallback((mode) => postToHost({ type: 'resize', mode }), [postToHost]);
  const requestClose = useCallback(() => postToHost({ type: 'close' }), [postToHost]);
  const publishSessionId = useCallback((sessionId) => postToHost({ type: 'session-id', sessionId }), [postToHost]);
  const publishError = useCallback((message, code) => postToHost({ type: 'error', message, code }), [postToHost]);
  const publishBranding = useCallback((branding) => postToHost({ type: 'branding', branding }), [postToHost]);

  return {
    awaitingConfirm, decideConfirm, hostFeatures, hostMobile, hasFeature,
    requestResize, requestClose, publishSessionId, publishError, publishBranding,
  };
}

export default useClientToolBridge;
